import type { ReactNode } from 'react'

import { buildLocalSearchStatusItems, buildGlobalSearchStatusItems } from './statusItemBuilders'

import type { AgentLoadingState } from './useAgentLoadingState'
import type { ChatStatusMessageItem } from './types/ChatStatusMessageTypes'

interface DeriveCtx {
    loadingState: AgentLoadingState
    messageList: { role?: string; label?: string; assistantHandover?: unknown; finishedMessage?: boolean }[]
    groupSize: number
    isGlobalPath: boolean
    browsePhaseVisible: boolean
    browsingChildVisible: boolean
    browsingDoneVisible: boolean
}

// The status items of an answer from what the chat knows (loading state, messages, which phases are visible)
export const deriveStatusItems = (t: (k: string) => string, ctx: DeriveCtx): ChatStatusMessageItem[] => {
    const { loadingState, messageList, groupSize, isGlobalPath, browsePhaseVisible, browsingChildVisible, browsingDoneVisible } = ctx
    if (loadingState === 'idle') return []

    const lastMsg = messageList[messageList.length - 1]
    const streamDone = lastMsg?.finishedMessage === true
    const effectiveLoadingState: AgentLoadingState = !browsingChildVisible
        ? 'optimistic_thinking'
        : (loadingState === 'optimistic_thinking' || !browsingDoneVisible)
            ? 'agent_thinking'
            : loadingState
    const isPanelLoading = loadingState === 'optimistic_thinking' || loadingState === 'agent_thinking'

    // Query type comes from the ChatLabel the backend sent for the current request
    const lastUserIndex = messageList.reduce((acc, m, i) => (m.role === 'user' ? i : acc), -1)
    const currentRequest = messageList.slice(lastUserIndex + 1)
    const isTroubleshootingQuery =
        currentRequest.find(m => !!m.label)?.label === 'Troubleshooting Solutions for Selected Lines' ||
        currentRequest.some(m => !!m.assistantHandover)

    if (isGlobalPath) {
        const timeStr = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
        const isBrowseVisible = browsePhaseVisible && loadingState !== 'optimistic_thinking'
        return buildGlobalSearchStatusItems(t, { effectiveLoadingState, isPanelLoading, isBrowseVisible, streamDone, groupSize, timeStr, isTroubleshootingQuery })
    }
    return buildLocalSearchStatusItems(t, { effectiveLoadingState, isPanelLoading, isBrowseVisible: browsePhaseVisible, streamDone, groupSize, isTroubleshootingQuery })
}

// Puts content under the solution item (the last item, if it is the solution)
export const injectContent = (items: ChatStatusMessageItem[], content: ReactNode): ChatStatusMessageItem[] => {
    const last = items[items.length - 1]
    if (!last || last.type !== 'solution-found') return items
    return [...items.slice(0, -1), { ...last, content }]
}
